import React from 'react';
import styles from './Sources.module.css';
import { MediaSource } from '@interfaces';
import { dedupeKey } from '@/lib/media/links';

export interface DeadLink {
  url: string;
  wayback?: string;
}

interface SourcesProps {
  media: MediaSource[];
  /** Broken links found in content (from reconcile_page_links). A source whose
   *  url matches one here is rendered against its Wayback archive (if any) with
   *  an "archived" badge; dead links with no archive are dropped. */
  deadlinks?: DeadLink[];
}

const imageExtensions = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.tiff',
  '.webp',
  '.svg',
];

const DEFAULT_THUMBNAIL = '/imgs/surface-waves/SURFACEWAVE/SURFACEWAVE.jpg';

function asText(value: unknown): string {
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ');
  if (value === null || value === undefined) return '';
  if (value === 'None') return '';
  return String(value).trim();
}

function getSiteName(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'external link';
  }
}

function isImageUrl(url: string): boolean {
  const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
  return imageExtensions.some((ext) => cleanUrl.endsWith(ext));
}

function isValidSource(item: MediaSource): boolean {
  const fullUrl = asText((item as any).url);

  if (!fullUrl || fullUrl === '#') return false;
  if (asText((item as any).type).toLowerCase() === 'image') return false;
  if (isImageUrl(fullUrl)) return false;

  return true;
}

// Derive the first-page scan thumbnail for an on-site PDF. Every ingested PDF
// carries a rasterised page tree at <dir>/pages/0001/image.png (same path the
// registry's thumb_url uses); a broken guess just falls back via the <img>
// onError handler below, so this is safe to attempt for any /pdfs/ link.
function pdfPageThumb(url: string): string {
  const clean = url.split('?')[0].split('#')[0];
  if (!clean.startsWith('/pdfs/')) return '';
  let dir = clean;
  if (dir.toLowerCase().endsWith('.pdf')) dir = dir.slice(0, dir.lastIndexOf('/'));
  dir = dir.replace(/\/+$/, '');
  return dir ? `${dir}/pages/0001/image.png` : '';
}

// A reliable per-link thumbnail for an external source: the site's own favicon.
function faviconThumb(url: string): string {
  try {
    const host = new URL(url).hostname;
    if (!host || host.endsWith('thedailydialectics.com')) return '';
    return `https://www.google.com/s2/favicons?domain=${host}&sz=128`;
  } catch {
    return '';
  }
}

function getSourceThumbnail(item: MediaSource): string {
  const sourcePreview = (item as any).source_preview || {};

  const directThumbnail =
    asText(sourcePreview.image) ||
    asText(sourcePreview.thumbnail) ||
    asText((item as any).thumbnail) ||
    asText((item as any).image) ||
    asText((item as any).img_url) ||
    asText((item as any).image_url) ||
    asText((item as any).og_image);
  if (directThumbnail) return directThumbnail;

  // Deterministic fallbacks so a source is never left with the generic default:
  const url = asText((item as any).url);
  const pdf = pdfPageThumb(url);            // on-site PDF -> its first-page scan
  if (pdf) return pdf;
  const fav = faviconThumb(url);            // external link -> its favicon
  if (fav) return fav;

  return DEFAULT_THUMBNAIL;
}

function getSourceTitle(item: MediaSource, fullUrl: string): string {
  const sourcePreview = (item as any).source_preview || {};

  return (
    asText((item as any).title) ||
    asText(sourcePreview.title) ||
    asText((item as any).filename) ||
    asText((item as any).basename) ||
    fullUrl
  );
}

function getSourceDescription(item: MediaSource): string {
  const sourcePreview = (item as any).source_preview || {};

  return (
    asText((item as any).description) ||
    asText(sourcePreview.description) ||
    asText((item as any).keywords) ||
    'Source reference'
  );
}

function getSourceSiteName(item: MediaSource, fullUrl: string): string {
  const sourcePreview = (item as any).source_preview || {};

  return (
    asText((item as any).siteName) ||
    asText(sourcePreview.siteName) ||
    asText(sourcePreview.site_name) ||
    getSiteName(fullUrl)
  );
}

// Dead/archived state for a source: prefer the per-entry flags the reconciler
// writes onto media items, falling back to the deadlinks[] map by dedupeKey.
function resolveDead(
  item: MediaSource,
  deadMap: Map<string, string>,
): { dead: boolean; wayback: string } {
  const url = asText((item as any).url);
  const perEntryDead = (item as any).dead === true;
  const perEntryWb = asText((item as any).wayback);
  if (perEntryDead) return { dead: true, wayback: perEntryWb || deadMap.get(dedupeKey(url)) || '' };
  if (deadMap.has(dedupeKey(url))) return { dead: true, wayback: deadMap.get(dedupeKey(url)) || '' };
  return { dead: false, wayback: '' };
}

const Sources: React.FC<SourcesProps> = ({ media, deadlinks }) => {
  if (!media || media.length === 0) return null;

  const deadMap = new Map<string, string>();
  for (const d of deadlinks || []) {
    if (d?.url) deadMap.set(dedupeKey(d.url), asText(d.wayback));
  }

  const seenKeys = new Set<string>();

  const sources = media
    .map((item) => ({ item, ...resolveDead(item, deadMap) }))
    .filter(({ item, dead, wayback }) => {
      if (!isValidSource(item)) return false;
      // dead with no archive to point at — drop it (still recorded in deadlinks)
      if (dead && !wayback) return false;

      const key = dedupeKey(asText((item as any).url));
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

  if (sources.length === 0) return null;

  return (
    <section className={styles.sources}>
      <h2 className={styles.sourcesHeading}>Sources</h2>

      <div className={styles.sourcesGrid}>
        {sources.map(({ item, dead, wayback }, index) => {
          const fullUrl = asText((item as any).url);
          const href = dead && wayback ? wayback : fullUrl;
          const title = getSourceTitle(item, fullUrl);
          const description = getSourceDescription(item);
          const thumbnail = getSourceThumbnail(item);
          const siteName = dead && wayback ? 'web.archive.org' : getSourceSiteName(item, fullUrl);

          return (
            <div key={`${fullUrl}-${index}`} className={styles.sourceCardWrap}>
              <a
                className={styles.sourceCard}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={dead && wayback ? `Archived snapshot of ${fullUrl}` : fullUrl}
              >
                <div className={styles.sourceThumbWrap}>
                  <img
                    className={styles.sourceThumb}
                    src={thumbnail}
                    alt={title}
                    loading="lazy"
                    onError={(event) => {
                      const img = event.currentTarget;

                      if (img.dataset.fallbackApplied === 'true') {
                        img.style.display = 'none';
                        return;
                      }

                      img.dataset.fallbackApplied = 'true';
                      img.src = DEFAULT_THUMBNAIL;
                    }}
                  />
                </div>

                <div className={styles.sourceBody}>
                  <div className={styles.sourceTitle}>
                    {title}
                    {dead && wayback ? (
                      <span className={styles.archivedBadge}>archived</span>
                    ) : null}
                  </div>

                  {description ? (
                    <div className={styles.sourceDescription}>
                      {description}
                    </div>
                  ) : null}

                  <div className={styles.sourceMeta}>
                    <span>{siteName}</span>
                  </div>
                </div>
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Sources;